import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import Cookies from 'js-cookie';
import '../styles/walletView.css';
import { FaWallet, FaRocket, FaArrowUp, FaCalendarAlt, FaFilter, FaTimes } from 'react-icons/fa';
import Notification from '../components/Notification';
import { WalletSkeleton } from '../components/Skeletons';
import ScrollToTopButton from '../components/ScrollToTopButton';
import { UserContext } from '../contexts/UserContext';

const WalletView = () => {
    const navigate = useNavigate();
    const { redirectToLogin } = useContext(UserContext);
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [activePromotions, setActivePromotions] = useState([]);
    const [zairePrices, setZairePrices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [filter, setFilter] = useState('all');
    const [sortDirection, setSortDirection] = useState('desc');
    const [userId, setUserId] = useState(null);
    const [userUuid, setUserUuid] = useState(null); // Add UUID state
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    // Date filter states
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDateFilterActive, setIsDateFilterActive] = useState(false);
    const [showDateFilter, setShowDateFilter] = useState(false);
    // Contact info state
    const [contactInfo, setContactInfo] = useState(null);
    const [loadingContactInfo, setLoadingContactInfo] = useState(true);
    
    // Cache management
    const CACHE_KEYS = {
        WALLET: 'wallet_data_cache',
        TRANSACTIONS: 'wallet_transactions_cache',
        PROMOTIONS: 'wallet_promotions_cache',
        CONTACT: 'wallet_contact_cache',
        TIMESTAMP: 'wallet_cache_timestamp'
    };
    const CACHE_TTL = 2 * 60 * 1000; // 2 minutes cache TTL
    
    // Request deduplication
    const fetchInProgressRef = useRef(false);
    const abortControllerRef = useRef(null);

    // Cache utilities
    const saveToCache = useCallback((key, data) => {
        try {
            localStorage.setItem(key, JSON.stringify(data));
            localStorage.setItem(CACHE_KEYS.TIMESTAMP, Date.now().toString());
        } catch (error) {

        }
    }, []);

    const getFromCache = useCallback((key) => {
        try {
            const timestamp = localStorage.getItem(CACHE_KEYS.TIMESTAMP);
            if (timestamp && (Date.now() - parseInt(timestamp)) < CACHE_TTL) {
                const cached = localStorage.getItem(key);
                return cached ? JSON.parse(cached) : null;
            }
        } catch (error) {

        }
        return null;
    }, []);

    const clearWalletCache = useCallback(() => {
        Object.values(CACHE_KEYS).forEach(key => {
            try {
                localStorage.removeItem(key);
            } catch (error) {

            }
        });
    }, []);

    useEffect(() => {
        // Load cached data first for instant display
        const loadCachedData = () => {
            const cachedWallet = getFromCache(CACHE_KEYS.WALLET);
            const cachedTransactions = getFromCache(CACHE_KEYS.TRANSACTIONS);
            const cachedPromotions = getFromCache(CACHE_KEYS.PROMOTIONS);
            const cachedContact = getFromCache(CACHE_KEYS.CONTACT);
            
            if (cachedWallet) {
                setWallet(cachedWallet);
                setUserId(cachedWallet.user);
            }
            if (cachedTransactions) {
                setTransactions(cachedTransactions);
            }
            if (cachedPromotions) {
                setActivePromotions(cachedPromotions);
            }
            if (cachedContact) {
                setContactInfo(cachedContact);
                setLoadingContactInfo(false); // Cached contact info available
            }
            
            // If we have cached data, show it immediately
            if (cachedWallet || cachedTransactions) {
                setLoading(false);
            }
        };

        const fetchWalletData = async () => {
            // Prevent duplicate requests
            if (fetchInProgressRef.current) {

                return;
            }

            const token = Cookies.get('authToken');
            if (!token) {
                redirectToLogin(window.location.pathname);
                setLoading(false);
                return;
            }

            fetchInProgressRef.current = true;

            // Cancel previous request if exists
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();

            try {
                setLoading(true);
                
                // Fetch all data in parallel with 10s timeout
                const timeoutId = setTimeout(() => abortControllerRef.current.abort(), 10000);
                
                const [userResponse, walletResponse, contactInfoResponse] = await Promise.all([
                    fetch(`${config.backendUrl}/users/me/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        },
                        signal: abortControllerRef.current.signal
                    }),
                    fetch(`${config.backendUrl}/payments/wallet/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        },
                        signal: abortControllerRef.current.signal
                    }),
                    fetch(`${config.backendUrl}/contact-info/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        signal: abortControllerRef.current.signal
                    })
                ]);

                clearTimeout(timeoutId);

                if (!userResponse.ok) {
                    throw new Error('Échec de la récupération des informations utilisateur');
                }

                if (!walletResponse.ok) {
                    throw new Error('Échec de la récupération des informations du portefeuille');
                }

                const [userData, walletData, contactInfoData] = await Promise.all([
                    userResponse.json(),
                    walletResponse.json(),
                    contactInfoResponse.ok ? contactInfoResponse.json() : null
                ]);

                setUserId(userData.id);
                setUserUuid(userData.uuid); // Store UUID
                setWallet(walletData);
                saveToCache(CACHE_KEYS.WALLET, walletData);
                
                if (contactInfoData) {
                    setContactInfo(contactInfoData);
                    saveToCache(CACHE_KEYS.CONTACT, contactInfoData);
                }
                setLoadingContactInfo(false); // Mark contact info as loaded

                // Fetch transactions
                await fetchTransactions(walletData.id, 1);

                // Fetch additional data in parallel (non-critical)
                const [activePromotionsResponse, zairePricesResponse] = await Promise.allSettled([
                    fetch(`${config.backendUrl}/payments/promotions/active/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        },
                        signal: abortControllerRef.current.signal
                    }),
                    fetch(`${config.backendUrl}/payments/prices/list/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        },
                        signal: abortControllerRef.current.signal
                    })
                ]);

                // Handle active promotions
                if (activePromotionsResponse.status === 'fulfilled' && activePromotionsResponse.value.ok) {
                    const activePromotionsData = await activePromotionsResponse.value.json();
                    setActivePromotions(activePromotionsData || []);
                    saveToCache(CACHE_KEYS.PROMOTIONS, activePromotionsData || []);
                }

                // Handle Zaire prices
                if (zairePricesResponse.status === 'fulfilled' && zairePricesResponse.value.ok) {
                    const zairePricesData = await zairePricesResponse.value.json();
                    setZairePrices(zairePricesData || []);
                }

            } catch (err) {
                if (err.name === 'AbortError') {

                    return;
                }
                console.error('Error:', err);
                setError(err.message);
                setNotification({
                    message: err.message,
                    type: 'error'
                });
            } finally {
                setLoading(false);
                setLoadingContactInfo(false); // Ensure loading state is cleared
                fetchInProgressRef.current = false;
            }
        };

        // Load cached data first, then fetch fresh data
        loadCachedData();
        fetchWalletData();

        // Cleanup
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    // Optimize fetchTransactions with better error handling and loading states
    const fetchTransactions = async (walletId, pageNum) => {
        const token = Cookies.get('authToken');
        if (!token) return;
        
        if (pageNum > 1) setLoadingMore(true);
        
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout
            
            const response = await fetch(
                `${config.backendUrl}/payments/wallet/transactions/${walletId}/?page=${pageNum}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    },
                    signal: controller.signal
                }
            );

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error('Échec de la récupération des transactions');
            }

            const data = await response.json();
            setHasMore(!!data.next);
            if (pageNum === 1) {
                setTransactions(data.results || []);
                saveToCache(CACHE_KEYS.TRANSACTIONS, data.results || []);
            } else {
                setTransactions(prev => [...prev, ...(data.results || [])]);
            }
        } catch (error) {
            if (error.name === 'AbortError') {

                return;
            }
            console.error('Error fetching transactions:', error);
            setNotification({
                message: 'Erreur lors du chargement des transactions',
                type: 'error'
            });
            setHasMore(false); // Stop trying to load more if there's an error
        } finally {
            if (pageNum > 1) setLoadingMore(false);
        }
    };

    // Optimized transaction icon function with memoization
    const getTransactionIcon = (type) => {
        const iconMap = {
            'SPONSORISER': <FaRocket className="transaction-icon promotion" />,
            'SPONSOR': <FaRocket className="transaction-icon promotion" />,
            'BOOSTER': <FaArrowUp className="transaction-icon boost" />,
            'BOOST': <FaArrowUp className="transaction-icon boost" />,
            'PROMOTION': <FaRocket className="transaction-icon promotion" />,
            'DEPOSIT': <FaWallet className="transaction-icon deposit" />,
            'WITHDRAWAL': <FaArrowUp className="transaction-icon withdrawal" />,
        };
        
        return iconMap[type?.toUpperCase()] || <FaWallet className="transaction-icon" />;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    const getTransactionTypeLabel = (type) => {
        const translations = {
            'SPONSORISER': 'Sponsorisation',
            'SPONSOR': 'Sponsorisation',
            'BOOSTER': 'Boost',
            'BOOST': 'Boost',
            'PROMOTION': 'Promotion',
            'PAYMENT': 'Paiement',
            'PURCHASE': 'Achat',
            'REFUND': 'Remboursement',
            'DEPOSIT': 'Dépôt',
            'WITHDRAWAL': 'Retrait',
            'TRANSFER': 'Transfert',
            'CREDIT': 'Crédit',
            'DEBIT': 'Débit',
            'FEE': 'Frais',
            'COMMISSION': 'Commission',
            'BONUS': 'Bonus',
            'REWARD': 'Récompense',
        };
        
        return translations[type?.toUpperCase()] || type;
    };

    const handleFilterChange = (e) => {
        setFilter(e.target.value);
    };

    const toggleSortDirection = () => {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    };

    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
    };

    const applyDateFilter = () => {
        if (startDate || endDate) {
            setIsDateFilterActive(true);
            setShowDateFilter(false); // Hide the date filter panel after applying
        }
    };

    const clearDateFilter = () => {
        setStartDate('');
        setEndDate('');
        setIsDateFilterActive(false);
    };

    const toggleDateFilterPanel = () => {
        setShowDateFilter(!showDateFilter);
    };

    // Optimized filtering and sorting with useMemo for better performance
    const filteredAndSortedTransactions = React.useMemo(() => {
        const filtered = transactions.filter(transaction => {
            // Type filter
            const typeMatches = filter === 'all' || transaction.transaction_type === filter;

            // Date filter
            let dateMatches = true;
            if (isDateFilterActive) {
                const transactionDate = new Date(transaction.created_at);

                if (startDate) {
                    const startFilterDate = new Date(startDate);
                    startFilterDate.setHours(0, 0, 0, 0);
                    if (transactionDate < startFilterDate) {
                        dateMatches = false;
                    }
                }

                if (endDate) {
                    const endFilterDate = new Date(endDate);
                    endFilterDate.setHours(23, 59, 59, 999);
                    if (transactionDate > endFilterDate) {
                        dateMatches = false;
                    }
                }
            }

            return typeMatches && dateMatches;
        });

        // Sort transactions
        return [...filtered].sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }, [transactions, filter, isDateFilterActive, startDate, endDate, sortDirection]);

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '' });
    };

    if (loading) {
        return <WalletSkeleton />;
    }

    if (error) {
        return (
            <div className="wallet-container error">
                <Notification
                    message={error}
                    type="error"
                    onClose={handleNotificationClose}
                />
                <button onClick={() => window.location.reload()} className="retry-button">
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <div className="wallet-container">
            <Notification
                message={notification.message}
                type={notification.type}
                onClose={handleNotificationClose}
            />
            <div className="wallet-header">
                <div className="wallet-title">
                    <FaWallet className="wallet-icon" />
                    <h1>Mon Portefeuille Zaire</h1>
                </div>
                <p className="wallet-description">
                    Gérez vos Zaires et suivez vos transactions pour promouvoir vos produits
                </p>
            </div>

            <div className="wallet-balance-card">
                <div className="balance-info">
                    <h2>Solde actuel</h2>
                    <div className="balance-amount">{wallet?.balance} <span>Zaires</span></div>
                    <p className="balance-updated">Dernière mise à jour: {formatDate(wallet?.last_updated)}</p>
                </div>
                <div className="balance-actions">
                    <button className="promote-action" onClick={() => navigate(`/products/user/${userUuid || userId}/`)}>
                        <FaRocket /> Promouvoir un produit
                    </button>
                </div>
            </div>

            <div className="wallet-topup-section">
                <div className="topup-card-compact">
                    <div className="topup-content-flex">
                        <div className="topup-text-content">
                            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                                💰 Besoin de recharger votre portefeuille?
                            </h3>
                            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
                                Rechargez facilement via Mobile Money ou contactez-nous
                            </p>
                        </div>
                        <div className="topup-actions-flex">
                            <button 
                                onClick={() => navigate('/payment-topup')}
                                className="payment-button-compact"
                                style={{
                                    backgroundColor: '#003366',
                                    color: 'white',
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                    transition: 'background-color 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#002244'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#003366'}
                            >
                                💳 Recharger
                            </button>
                            {loadingContactInfo ? (
                                <div 
                                    style={{
                                        backgroundColor: '#e2e8f0',
                                        border: '1px solid #cbd5e1',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '110px'
                                    }}
                                >
                                    <div className="wallet-loading-spinner" style={{ width: '20px', height: '20px', borderWidth: '2px', margin: 0 }}></div>
                                </div>
                            ) : contactInfo?.whatsapp_link ? (
                                <a 
                                    href={contactInfo.whatsapp_link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{
                                        backgroundColor: '#25D366',
                                        color: 'white',
                                        padding: '10px 20px',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        textDecoration: 'none',
                                        whiteSpace: 'nowrap',
                                        transition: 'background-color 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#1eae58'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#25D366'}
                                >
                                    📱 WhatsApp
                                </a>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            {activePromotions.length > 0 && (
                <div className="active-promotions-section">
                    <h2>Promotions Actives</h2>
                    <div className="promotions-grid">
                        {activePromotions.map(promotion => (
                            <div key={promotion.id} className="promotion-card">
                                <div className="promotion-type">
                                    {getTransactionIcon(promotion.promote_type)}
                                    <span>{promotion.promote_type}</span>
                                </div>
                                <p className="promotion-product">Produit: {promotion.product.name}</p>
                                <p className="promotion-dates">
                                    Du {formatDate(promotion.start_date)} au {formatDate(promotion.end_date)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {zairePrices.length > 0 && (
                <div className="zaire-prices-section">
                    <h2>Tarifs des Promotions</h2>
                    <div className="prices-grid">
                        {zairePrices.map(price => (
                            <div key={price.id} className="price-card">
                                <h3>{price.promote_type}</h3>
                                <p className="price-amount">{price.price} Zaires</p>
                                <p className="price-duration">{price.duration} jours</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="transactions-section">
                <div className="transactions-header">
                    <h2>Historique des transactions</h2>
                    <div className="transaction-controls">
                        <div className="filter-control">
                            <label htmlFor="transaction-filter">Filtrer par type:</label>
                            <select id="transaction-filter" value={filter} onChange={handleFilterChange}>
                                <option value="all">Toutes les transactions</option>
                                <option value="SPONSORISER">Sponsoriser</option>
                                <option value="BOOSTER">Booster</option>
                                <option value="DEPOSIT">Dépôt</option>
                            </select>
                        </div>

                        <div className="date-filter-container">
                            <button
                                className={`filter-button ${isDateFilterActive ? 'active' : ''}`}
                                onClick={toggleDateFilterPanel}
                            >
                                <FaCalendarAlt /> Filtre par date
                            </button>

                            {isDateFilterActive && (
                                <div className="active-filters">
                                    <span>
                                        {startDate && `Du ${new Date(startDate).toLocaleDateString('fr-FR')}`}
                                        {endDate && startDate && ' '}
                                        {endDate && `au ${new Date(endDate).toLocaleDateString('fr-FR')}`}
                                    </span>
                                    <button className="clear-filter" onClick={clearDateFilter}>
                                        <FaTimes />
                                    </button>
                                </div>
                            )}

                            {showDateFilter && (
                                <div className="date-filter-panel">
                                    <div className="date-inputs">
                                        <div className="date-input-group">
                                            <label htmlFor="start-date">Date de début:</label>
                                            <input
                                                type="date"
                                                id="start-date"
                                                value={startDate}
                                                onChange={handleStartDateChange}
                                            />
                                        </div>
                                        <div className="date-input-group">
                                            <label htmlFor="end-date">Date de fin:</label>
                                            <input
                                                type="date"
                                                id="end-date"
                                                value={endDate}
                                                min={startDate || undefined}
                                                onChange={handleEndDateChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="date-filter-actions">
                                        <button onClick={clearDateFilter} className="clear-button">
                                            Effacer
                                        </button>
                                        <button onClick={applyDateFilter} className="apply-button">
                                            Appliquer
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button className="sort-button" onClick={toggleSortDirection}>
                            Trier par date {sortDirection === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>
                </div>

                {filteredAndSortedTransactions.length > 0 ? (
                    <div className="transactions-table-container">
                        <div className="table-responsive">
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th className="type-col">Type</th>
                                        <th className="date-col">Date</th>
                                        <th className="desc-col">Description</th>
                                        <th className="amount-col">Montant</th>
                                        <th className="balance-col">Solde</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAndSortedTransactions.map(transaction => (
                                        <tr key={transaction.id} className={`transaction-row ${transaction.amount < 0 ? 'negative' : 'positive'}`}>
                                            <td className="transaction-type type-col">
                                                <div className="type-content">
                                                    {getTransactionIcon(transaction.transaction_type)}
                                                    <span className="type-label">{getTransactionTypeLabel(transaction.transaction_type)}</span>
                                                </div>
                                            </td>
                                            <td className="date-col">{formatDate(transaction.created_at)}</td>
                                            <td className="transaction-description desc-col">{transaction.description}</td>
                                            <td className={`transaction-amount amount-col ${transaction.amount < 0 ? 'negative' : 'positive'}`}>
                                                {transaction.amount > 0 ? '+' : ''}{transaction.amount} Z
                                            </td>
                                            <td className="transaction-balance balance-col">{transaction.balance_after} Z</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="no-transactions">
                        <p>Aucune transaction {filter !== 'all' ? `de type ${getTransactionTypeLabel(filter)}` : ''} trouvée.</p>
                        {isDateFilterActive && <p>Essayez de modifier les filtres de date.</p>}
                    </div>
                )}

                {hasMore && (
                    <div className="load-more-container">
                        <button
                            className="load-more-button"
                            onClick={() => {
                                setPage(prev => prev + 1);
                                fetchTransactions(wallet.id, page + 1);
                            }}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Chargement...' : 'Charger plus de transactions'}
                        </button>
                    </div>
                )}
            </div>

            <ScrollToTopButton />
        </div>
    );
};

export default WalletView;